import { useMemo } from 'react';

import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { format, parseISO } from 'date-fns';

import { Box, Button, Chip, Dialog, Typography } from '@myn/mui';

import { useSendReminderEmail } from '../../../api/mutations/index.js';
import { useApprovalProcessDetails } from '../../../api/queries/index.js';
import { ApprovalFlowsStatusData } from '../../../schemas.js';
import type { InnerObject } from '../../../types.js';
import UserList from './ApproversList.jsx';
import { formatApprovers, type Approver } from './utils.js';

const renderChipByStatus = (status: string, t: TFunction) => {
  switch (status) {
    case 'approved':
      return <Chip label={t('Approved')} color="success" variant="outlined" />;
    case 'rejected':
      return (
        <Chip
          label={t('Rejected')}
          color="error"
          size="small"
          variant="outlined"
        />
      );
    default:
      return (
        <Chip
          label={t('Pending')}
          color="info"
          size="small"
          variant="outlined"
        />
      );
  }
};

type MoreDetailsModalProps = {
  onClose: () => void;
  open: boolean;
  innerObj: InnerObject;
  approvalFlowsStatusData: ApprovalFlowsStatusData;
};

const MoreDetailsModal: React.FC<MoreDetailsModalProps> = ({
  onClose,
  open,
  innerObj,
  approvalFlowsStatusData,
}: MoreDetailsModalProps) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit');
  const { mutateAsync: sendReminderEmail } = useSendReminderEmail();
  const { data: moreDetailsData } = useApprovalProcessDetails({
    approvalItemId: innerObj.objectId,
    approvalItemType: innerObj.type,
  });

  const data = useMemo(() => {
    if (!moreDetailsData) {
      return [];
    }

    const approversList = formatApprovers(moreDetailsData);

    return approversList;
  }, [moreDetailsData]);

  const handleApproverReminder = async (approver: Approver) => {
    if (!approvalFlowsStatusData?.approvalProcessId) {
      return;
    }

    await sendReminderEmail({
      approverId: approver.id,
      approvalProcessId: approvalFlowsStatusData.approvalProcessId,
    });

    enqueueSnackbar({
      title:
        approver.approvalType === 'operation'
          ? t('Reminder emails have been successfully sent.')
          : t('You just sent a reminder email to {{name}}', {
              name: approver.name,
            }),
      variant: 'success',
      removable: true,
    });
  };

  return (
    <Dialog
      title={t('Status details')}
      open={open}
      onClose={onClose}
      submitButton={<div />}
      cancelButtonProps={{ label: t('Cancel'), onClick: onClose }}
    >
      {data.map((approver) => {
        return (
          <Box
            key={approver.id}
            sx={{
              display: 'flex',
              mb: 1,
              alignItems: 'center',
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            {approver.approvalType !== 'operation' ? (
              <Typography variant="h6" width={233}>
                {approver.name}
              </Typography>
            ) : (
              <Box width={233}>
                <UserList users={approver.approvers ?? []} />
              </Box>
            )}
            {approver.status !== 'progress' ? (
              <Typography variant="caption01">
                {format(parseISO(approver.updatedAt), 'dd/MM/yyyy - HH:mm')}
              </Typography>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                disabled={!approver.canBeReminded}
                onClick={() => handleApproverReminder(approver)}
              >
                {t('Send a reminder email')}
              </Button>
            )}
            {renderChipByStatus(approver.status, t)}
          </Box>
        );
      })}
    </Dialog>
  );
};

export default MoreDetailsModal;
